/**
 * Centralized Permission System for DMV Soundstage
 * 
 * This module defines the permissions matrix for Role + Tier based access control.
 * All permission checks should go through this module for consistency.
 * 
 * Roles: listener, artist, organization, studio, podcaster, streamer, admin, owner
 * Artist Tiers: starter, growth, pro, elite, executive, label
 * 
 * Permission Matrix:
 * | Action     | Listener | Artist Free | Artist Pro+ | Organization | Admin/Owner |
 * |------------|----------|-------------|-------------|--------------|-------------|
 * | Like       | ✅       | ✅          | ✅          | ✅           | ✅          |
 * | Share      | ✅       | ✅          | ✅          | ✅           | ✅          |
 * | Comment    | ✅       | Rate-limit  | ✅ Full     | ✅           | ✅          |
 * | Tag        | Mutual   | Mutual      | ✅ Full     | Mutual       | ✅ Full     |
 */

// ===================== TIER DEFINITIONS =====================

// Tiers ordered from lowest to highest
export const TIER_HIERARCHY = [
  'starter',    // Free tier
  'growth',     // Basic paid
  'pro',        // Pro tier  
  'elite',      // Pro+ tier
  'executive',  // Executive tier
  'label'       // Label/Enterprise tier
];

// Tiers that get full permissions (Pro and above)
export const FULL_PERMISSION_TIERS = ['pro', 'elite', 'executive', 'label'];

// Roles that are always allowed full permissions
export const ADMIN_ROLES = ['admin', 'owner'];

// Creator roles (not listeners)
export const CREATOR_ROLES = [
  'artist', 'podcaster', 'streamer', 'studio', 
  'videographer', 'engineer', 'graphic_designer', 'producer', 'pr'
];

// Organization roles
export const ORG_ROLES = ['organization', 'studio'];

// ===================== RATE LIMITS =====================

export const COMMENT_RATE_LIMITS = {
  free_artist: {
    max_per_hour: 5,
    max_per_day: 20,
  },
  listener: {
    max_per_hour: 30,
    max_per_day: 100,
  }
};

// ===================== TIER DISPLAY NAMES =====================

export const TIER_DISPLAY_NAMES = {
  starter: 'Free',
  growth: 'Growth',
  pro: 'Pro',
  elite: 'Pro+',
  executive: 'Executive',
  label: 'Label / Enterprise'
};

// ===================== HELPER FUNCTIONS =====================

/**
 * Get the user's artist tier, defaulting to 'starter' (free)
 */
export function getUserTier(user) {
  return user?.artist_tier || user?.artistTier || 'starter';
}

/**
 * Get the user's primary role
 */
export function getUserRole(user) {
  return user?.role || user?.primaryRole || 'listener';
}

/**
 * Check if user is admin or owner
 */
export function isAdminOrOwner(user) {
  if (!user) return false;
  const role = getUserRole(user);
  const isOwner = user.is_owner || user.isOwner || user.isPlatformOwner || false;
  return ADMIN_ROLES.includes(role) || isOwner;
}

/**
 * Check if user has a full permission tier (Pro and above)
 */
export function isFullTier(user) {
  if (!user) return false;
  if (isAdminOrOwner(user)) return true;
  const tier = getUserTier(user);
  return FULL_PERMISSION_TIERS.includes(tier);
}

/**
 * Check if user is in a creator role
 */
export function isCreatorRole(user) {
  if (!user) return false;
  const role = getUserRole(user);
  return CREATOR_ROLES.includes(role);
}

/**
 * Check if user is an organization
 */
export function isOrganization(user) {
  if (!user) return false;
  const role = getUserRole(user);
  return ORG_ROLES.includes(role);
}

/**
 * Get display name for a tier
 */
export function getTierDisplayName(tier) {
  return TIER_DISPLAY_NAMES[tier] || 'Free';
}

// ===================== MAIN PERMISSION FUNCTIONS =====================

/**
 * Check if user can like content
 * Everyone can like unless banned
 * 
 * @param {Object} user - The user object
 * @returns {{ allowed: boolean, reason: string }}
 */
export function canLike(user) {
  if (!user) {
    return { allowed: false, reason: 'Please sign in to like' };
  }
  if (user.is_banned || user.isBanned) {
    return { allowed: false, reason: 'Your account has been suspended' };
  }
  return { allowed: true, reason: '' };
}

/**
 * Check if user can share content
 * Everyone can share unless banned
 * 
 * @param {Object} user - The user object
 * @returns {{ allowed: boolean, reason: string }}
 */
export function canShare(user) {
  if (!user) {
    return { allowed: false, reason: 'Please sign in to share' };
  }
  if (user.is_banned || user.isBanned) {
    return { allowed: false, reason: 'Your account has been suspended' };
  }
  return { allowed: true, reason: '' };
}

/**
 * Check if user can comment on content
 * 
 * @param {Object} user - The user object
 * @param {Object} commentStatus - Optional object with remaining/limit from backend
 * @returns {{
 *   allowed: boolean,
 *   rateLimited: boolean,
 *   remaining: number|null,
 *   limit: number|null,
 *   reason: string,
 *   upgradeMessage: string
 * }}
 */
export function canComment(user, commentStatus = null) {
  const result = {
    allowed: true,
    rateLimited: false,
    remaining: null,
    limit: null,
    reason: '',
    upgradeMessage: ''
  };

  // Not signed in
  if (!user) {
    result.allowed = false;
    result.reason = 'Please sign in to comment';
    return result;
  }

  // Banned
  if (user.is_banned || user.isBanned) {
    result.allowed = false;
    result.reason = 'Your account has been suspended';
    return result;
  }

  // Comment suspended
  const suspendedUntil = user.comment_suspended_until || user.commentSuspendedUntil;
  if (suspendedUntil) {
    try {
      const suspendedDate = new Date(suspendedUntil);
      if (suspendedDate > new Date()) {
        result.allowed = false;
        result.reason = 'Your commenting privileges are temporarily suspended';
        return result;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  const role = getUserRole(user);

  // Admin/Owner: Full access
  if (isAdminOrOwner(user)) {
    return result;
  }

  // Listener: Full access
  if (role === 'listener') {
    return result;
  }

  // Organization: Full access
  if (isOrganization(user)) {
    return result;
  }

  // Creator roles: Check tier
  if (isCreatorRole(user)) {
    const tier = getUserTier(user);

    // Pro and above: Full access
    if (FULL_PERMISSION_TIERS.includes(tier)) {
      return result;
    }

    // Free/starter tier: Rate limited
    result.rateLimited = true;
    result.limit = COMMENT_RATE_LIMITS.free_artist.max_per_hour;
    result.upgradeMessage = 'Upgrade to Pro for unlimited comments';

    // If we have status from backend, use it
    if (commentStatus) {
      result.remaining = commentStatus.remaining;
      result.limit = commentStatus.limit || result.limit;
      if (commentStatus.remaining !== undefined && commentStatus.remaining <= 0) {
        result.allowed = false;
        result.reason = `Comment limit reached (${result.limit}/hour). Upgrade to Pro for unlimited comments.`;
      }
    }

    return result;
  }

  // Default: Allow
  return result;
}

/**
 * Check if user can tag another user
 * 
 * @param {Object} user - The user trying to tag
 * @param {Object} targetUser - The user being tagged (optional)
 * @param {Object} followsData - { userFollowsTarget: boolean, targetFollowsUser: boolean }
 * @returns {{ allowed: boolean, reason: string, requiresMutualFollow: boolean }}
 */
export function canTag(user, targetUser = null, followsData = null) {
  const result = {
    allowed: true,
    reason: '',
    requiresMutualFollow: false
  };

  if (!user) {
    result.allowed = false;
    result.reason = 'Please sign in to tag users';
    return result;
  }

  if (user.is_banned || user.isBanned) {
    result.allowed = false;
    result.reason = 'Your account has been suspended';
    return result;
  }

  // Admin/Owner: Can tag anyone
  if (isAdminOrOwner(user)) {
    return result;
  }

  // Pro+ tier: Can tag anyone
  if (isFullTier(user)) {
    return result;
  }

  // Others need mutual follow
  result.requiresMutualFollow = true;

  if (followsData) {
    const { userFollowsTarget, targetFollowsUser } = followsData;
    if (!userFollowsTarget && !targetFollowsUser) {
      result.allowed = false;
      result.reason = 'You can only tag users who follow you or whom you follow';
    }
  }

  return result;
}

/**
 * Check if user can reply to comments
 * Same rules as commenting
 */
export function canReply(user, commentStatus = null) {
  return canComment(user, commentStatus);
}

// ===================== PERMISSION INFO FOR UI =====================

/**
 * Get complete permission info for a user to display in UI
 * 
 * @param {Object} user - The user object
 * @param {Object} commentStatus - Optional status from backend with remaining/limit
 * @returns {Object} Complete permission state
 */
export function getUserPermissions(user, commentStatus = null) {
  if (!user) {
    return {
      role: null,
      tier: null,
      tierName: null,
      isAdmin: false,
      isFullTier: false,
      isCreator: false,
      authenticated: false,
      like: { allowed: false },
      share: { allowed: false },
      comment: { allowed: false, rateLimited: false },
      tag: { allowed: false, requiresMutualFollow: true },
      reply: { allowed: false, rateLimited: false }
    };
  }

  const role = getUserRole(user);
  const tier = getUserTier(user);
  const commentPerm = canComment(user, commentStatus);
  const likePerm = canLike(user);
  const sharePerm = canShare(user);
  const tagPerm = canTag(user);

  return {
    role,
    tier,
    tierName: getTierDisplayName(tier),
    isAdmin: isAdminOrOwner(user),
    isFullTier: isFullTier(user),
    isCreator: isCreatorRole(user),
    authenticated: true,

    like: {
      allowed: likePerm.allowed,
      unlimited: true
    },

    share: {
      allowed: sharePerm.allowed,
      unlimited: true
    },

    comment: {
      allowed: commentPerm.allowed,
      rateLimited: commentPerm.rateLimited,
      remaining: commentPerm.remaining,
      limitPerHour: commentPerm.limit,
      upgradeMessage: commentPerm.upgradeMessage,
      reason: commentPerm.reason
    },

    tag: {
      allowed: tagPerm.allowed,
      requiresMutualFollow: tagPerm.requiresMutualFollow,
      unlimited: !tagPerm.requiresMutualFollow
    },

    reply: {
      allowed: commentPerm.allowed,
      rateLimited: commentPerm.rateLimited,
      remaining: commentPerm.remaining,
      limitPerHour: commentPerm.limit
    }
  };
}

/**
 * Get a brief summary of user's permission level
 */
export function getPermissionSummary(user) {
  if (!user) return 'Sign in to interact';

  if (isAdminOrOwner(user)) {
    return 'Full access (Admin/Owner)';
  }

  const role = getUserRole(user);
  const tier = getUserTier(user);

  if (role === 'listener') {
    return 'Full access (Listener)';
  }

  if (isOrganization(user)) {
    return 'Full access (Organization)';
  }

  if (FULL_PERMISSION_TIERS.includes(tier)) {
    return `Full access (${getTierDisplayName(tier)})`;
  }

  return `Standard access (${getTierDisplayName(tier)}) - Comments limited to ${COMMENT_RATE_LIMITS.free_artist.max_per_hour}/hour`;
}

/**
 * Format remaining comments display
 */
export function formatRemainingComments(remaining, limit) {
  if (remaining === null || remaining === undefined) return null;
  return `${remaining}/${limit} comments left this hour`;
}
